import { useEffect } from "react";
import { usePageSEO } from "../hooks/usePageSEO";
import { Link } from "react-router-dom";
import { Instagram, Award, Cpu, Briefcase, ChevronRight, Mail } from "lucide-react";
import { LOGO_URL } from "../lib/brand";

const TEAM = [
  {
    id: "adrian",
    name: "Adrian Bienz",
    role: "Co-Founder · Head of Trading",
    img: "/team-adrian.jpg",
    intro:
      "Trader & Co-Founder. Adrian entwickelt und überwacht das KI-Tradingsystem hinter Alphalane.",
    stats: [
      { icon: Briefcase, label: "Börsen­erfahrung", value: "10+ Jahre" },
      { icon: Award, label: "Ausgebildet von", value: "Hedge-Fonds-Veteranen" },
      { icon: Cpu, label: "Roboter-Coding", value: "1500+ Stunden" },
    ],
    bullets: [
      "10 Jahre Börsenerfahrung in CFD, Futures und Aktien",
      "Ausgebildet von ehemaligen Hedge-Fonds-Managern (Nomura, Société Générale uvm.)",
      "Über 1500 Stunden Erfahrung in der Roboter-Programmierung",
      "Co-Founder Alphalane Trading Systems",
    ],
    instagram: "https://www.instagram.com/adrian_bienz/",
  },
  {
    id: "pascal",
    name: "Pascal Schwab",
    role: "Co-Founder · Geschäftsführer",
    img: "/team-pascal.jpg",
    intro:
      "Pascal verantwortet Strategie, Partner-Onboarding und das operative Wachstum von Alphalane.",
    stats: [
      { icon: Briefcase, label: "Geschäftsführer", value: "Moonshine Solutions GmbH" },
      { icon: Award, label: "Co-Founder", value: "Alphalane Trading Systems" },
      { icon: Cpu, label: "Experte für", value: "Vermögensaufbau" },
    ],
    bullets: [
      "Geschäftsführer Moonshine Solutions GmbH",
      "Co-Founder Alphalane Trading Systems",
      "Strategie, Partnerschaften & operatives Wachstum",
    ],
    instagram: "https://www.instagram.com/pascals.solutions/",
  },
];

export default function About() {
  usePageSEO({
    title: "Über uns — Pascal & Adrian · Alphalane Trading Systems",
    description: "Lerne die Gründer von Alphalane AI kennen: Pascal & Adrian. Vollautomatisiertes KI-Trading aus der Schweiz — transparent, regelbasiert, mit öffentlichem Track Record.",
    canonical: "https://alphalane-tradingsystems.com/about",
  });

  return (
    <div className="min-h-screen bg-[var(--al-bg)]" data-testid="about-page">
      <Header />

      <main className="max-w-6xl mx-auto px-5 sm:px-8 py-12 sm:py-20">
        {/* Hero */}
        <section className="mb-16 sm:mb-24 max-w-3xl">
          <span className="al-tag">Das Team</span>
          <h1 className="mt-4 font-display font-bold text-4xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight">
            Hinter Alphalane stehen <span className="al-grad-text">zwei Köpfe.</span>
          </h1>
          <p className="mt-6 text-base sm:text-lg text-slate-600 leading-relaxed">
            Wir verbinden jahrzehntelange Börsenexpertise mit modernster KI-Programmierung —
            damit du nicht raten musst, ob dein Geld arbeitet, sondern weißt, wie es das tut.
          </p>
        </section>

        {/* Team — Adrian + Pascal */}
        <div className="space-y-20 sm:space-y-28">
          {TEAM.map((member, idx) => (
            <TeamMember key={member.id} member={member} reversed={idx % 2 === 1} />
          ))}
        </div>

        {/* Mission */}
        <section className="mt-24 sm:mt-32 max-w-4xl mx-auto" data-testid="mission-section">
          <div className="al-card p-8 sm:p-12 bg-gradient-to-br from-[var(--al-grad-soft)] via-white to-violet-50/40 al-grad-soft-bg">
            <span className="al-tag">Unsere Mission</span>
            <h2 className="mt-4 font-display font-bold text-3xl sm:text-4xl tracking-tight">
              Algorithmisches Trading, <br className="hidden sm:block" />
              das wirklich für dich arbeitet.
            </h2>
            <p className="mt-5 text-base sm:text-lg text-slate-700 leading-relaxed max-w-2xl">
              Wir glauben, dass professionelles algorithmisches Trading nicht länger Hedge-Fonds
              vorbehalten sein darf. Mit Alphalane öffnen wir Privatanlegern den Zugang zu einer KI,
              die rund um die Uhr Märkte analysiert, Risiken steuert und Chancen nutzt — ohne dass
              du selbst zum Trader werden musst.
            </p>
            <Link
              to="/#kontakt"
              data-testid="mission-cta"
              className="mt-7 inline-flex items-center gap-2 px-6 py-3 rounded-full al-grad-bg text-white font-display font-semibold hover:opacity-90 transition-opacity shadow-md"
            >
              Jetzt unverbindlich anfragen <ChevronRight className="h-4 w-4" />
            </Link>
          </div>
        </section>
      </main>

      <footer className="border-t border-[var(--al-line)] bg-white/60 mt-12">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-6 text-xs text-slate-500 flex items-center justify-between flex-wrap gap-3">
          <div>© Alphalane Trading Systems · Moonshine Solutions GmbH · Schmitten FR, Schweiz</div>
          <Link to="/" className="hover:text-slate-900">Zurück zur Startseite →</Link>
        </div>
      </footer>
    </div>
  );
}

const Header = () => (
  <header className="border-b border-[var(--al-line)] bg-white/80 backdrop-blur-xl sticky top-0 z-30">
    <div className="max-w-6xl mx-auto px-5 sm:px-8 py-4 flex items-center justify-between gap-3">
      <Link to="/" className="flex items-center gap-3">
        <img src={LOGO_URL} alt="Alphalane" className="h-9 w-9 object-contain" />
        <div className="font-display font-semibold text-[15px]">
          <span className="al-grad-text">ALPHALANE</span>
          <span className="text-slate-500 text-xs ml-2 font-medium uppercase tracking-widest hidden sm:inline">Über uns</span>
        </div>
      </Link>
      <nav className="flex items-center gap-1 sm:gap-2 text-sm">
        <Link to="/" className="px-3 py-2 text-slate-600 hover:text-slate-900 font-medium">Start</Link>
        <Link to="/blog" className="px-3 py-2 text-slate-600 hover:text-slate-900 font-medium">Blog</Link>
        <Link to="/about" className="px-3 py-2 text-slate-900 font-semibold border-b-2 border-[var(--al-purple)]">Über uns</Link>
      </nav>
    </div>
  </header>
);

const TeamMember = ({ member, reversed }) => (
  <section
    className={`grid lg:grid-cols-2 gap-8 lg:gap-14 items-center ${reversed ? "lg:[&>*:first-child]:order-2" : ""}`}
    data-testid={`team-member-${member.id}`}
  >
    {/* Image */}
    <div className="relative">
      <div className="relative aspect-square rounded-3xl overflow-hidden shadow-2xl shadow-violet-900/10 ring-1 ring-black/5">
        <img src={member.img} alt={member.name} className="w-full h-full object-cover" />
      </div>
    </div>

    {/* Bio */}
    <div>
      <div className="text-xs uppercase tracking-widest font-bold text-[var(--al-purple)]">
        {member.id === "adrian" ? "Co-Founder · Trading" : "Co-Founder · Geschäftsleitung"}
      </div>
      <h2 className="mt-2 font-display font-bold text-4xl sm:text-5xl tracking-tight leading-tight">
        {member.name}
      </h2>
      <p className="mt-4 text-base sm:text-lg text-slate-700 leading-relaxed">
        {member.intro}
      </p>

      {/* Stats grid */}
      <div className="mt-7 grid sm:grid-cols-3 gap-3" data-testid={`${member.id}-stats`}>
        {member.stats.map((s, i) => {
          const Icon = s.icon;
          return (
            <div key={s.label || s.value || i} className="al-card p-4">
              <Icon className="h-4 w-4 text-[var(--al-purple)] mb-2" />
              <div className="text-[10px] uppercase tracking-widest font-bold text-slate-400">{s.label}</div>
              <div className="font-display font-bold text-sm text-slate-900 mt-1 leading-tight">{s.value}</div>
            </div>
          );
        })}
      </div>

      {/* Bullets */}
      <ul className="mt-6 space-y-2.5" data-testid={`${member.id}-bullets`}>
        {member.bullets.map((b, i) => (
          <li key={b} className="flex items-start gap-3 text-sm text-slate-700">
            <span className="mt-1.5 h-1.5 w-1.5 rounded-full al-grad-bg flex-shrink-0" />
            <span>{b}</span>
          </li>
        ))}
      </ul>

      {/* Instagram CTA */}
      <a
        href={member.instagram}
        target="_blank"
        rel="noreferrer"
        data-testid={`${member.id}-instagram`}
        className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-full border border-slate-200 hover:border-[var(--al-purple)] text-slate-700 hover:text-[var(--al-purple)] text-sm font-display font-semibold transition-all"
      >
        <Instagram className="h-4 w-4" /> @{member.instagram.split("/").filter(Boolean).pop()}
      </a>
    </div>
  </section>
);
